import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  FiFolder, 
  FiFile, 
  FiPlus, 
  FiTrash2, 
  FiChevronRight, 
  FiChevronDown,
  FiSearch,
  FiFolderPlus,
  FiFilePlus,
  FiX,
  FiSave,
  FiRefreshCw
} from 'react-icons/fi'
import { VscFolder, VscFolderOpened } from 'react-icons/vsc'
import { IoMdColorWand } from 'react-icons/io'
import FileTree from './FileTree'
import toast from 'react-hot-toast'

const Sidebar = ({ files, currentFile, onFileSelect, onFileCreate, onFileDelete, onToggle }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [newItemName, setNewItemName] = useState('')
  const [newItemType, setNewItemType] = useState('file')
  const [showNewItemModal, setShowNewItemModal] = useState(false)
  const [expandedFolders, setExpandedFolders] = useState(['/'])

  const toggleFolder = (path) => {
    setExpandedFolders(prev =>
      prev.includes(path)
        ? prev.filter(p => p !== path)
        : [...prev, path]
    )
  }

  const filteredFiles = Object.values(files).filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.path.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getFilesByPath = (path) => {
    return filteredFiles.filter(file => 
      file.path === path && file.type === 'file'
    )
  }

  const getFolders = () => {
    const folders = new Set(['/'])
    filteredFiles.forEach(file => {
      if (file.path.includes('/')) {
        const parts = file.path.split('/').filter(Boolean)
        let currentPath = ''
        parts.forEach(part => {
          currentPath += `/${part}`
          folders.add(currentPath)
        })
      }
    })
    return Array.from(folders)
  }

  const handleCreateItem = () => {
    if (!newItemName.trim()) {
      toast.error('Please enter a name')
      return
    }

    const path = '/'
    const fullName = newItemType === 'file' 
      ? (newItemName.includes('.') ? newItemName : `${newItemName}.js`)
      : newItemName

    onFileCreate(newItemType, fullName, path)
    setNewItemName('')
    setShowNewItemModal(false)
    toast.success(`${newItemType === 'file' ? 'File' : 'Folder'} created!`)
  }

  const handleDeleteFile = (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      onFileDelete(fileId)
    }
  }

  return (
    <div className="h-full flex flex-col bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border-r border-gray-200 dark:border-gray-800">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <VscFolder className="text-primary-600 dark:text-primary-400 text-xl" />
            <h2 className="font-bold text-gray-900 dark:text-white">Explorer</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowNewItemModal(true)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title="New File/Folder"
            >
              <FiPlus className="text-gray-600 dark:text-gray-400" />
            </button>
            <button
              onClick={onToggle}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors lg:hidden"
            >
              <FiX className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
          />
        </div>
      </div>

      {/* File Tree */}
      <div className="flex-1 overflow-y-auto p-4">
        <FileTree
          files={files}
          currentFile={currentFile}
          onFileSelect={onFileSelect}
          onFileDelete={handleDeleteFile}
          expandedFolders={expandedFolders}
          onToggleFolder={toggleFolder}
        />
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-800">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center justify-between mb-2">
            <span>Total Files: {Object.keys(files).length}</span>
            <button
              onClick={() => {
                setExpandedFolders(['/'])
                setSearchTerm('')
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              title="Refresh"
            >
              <FiRefreshCw className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* New Item Modal */}
      {showNewItemModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Create New
              </h3>
              <button
                onClick={() => setShowNewItemModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <FiX className="text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <div className="mb-6">
              <div className="flex space-x-2 mb-4">
                {['file', 'folder'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setNewItemType(type)}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                      newItemType === type
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {type === 'file' ? (
                      <span className="flex items-center justify-center space-x-2">
                        <FiFile />
                        <span>File</span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center space-x-2">
                        <FiFolder />
                        <span>Folder</span>
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder={
                  newItemType === 'file'
                    ? "Filename (e.g., app.js, style.css)"
                    : "Folder name"
                }
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 text-gray-900 dark:text-white"
                autoFocus
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowNewItemModal(false)}
                className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateItem}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-primary-500 to-red-500 text-white font-medium rounded-lg hover:from-primary-600 hover:to-red-600 transition-all shadow-lg"
              >
                Create
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default Sidebar